import {
    Component,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Control, IqControlsDashboardComponent } from '@dat/iq-controls';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { DatiqFeatureFlagStateService } from '@dat/datiq-feature-flag';
import { lastValueFrom, withLatestFrom } from 'rxjs';
import { flatten, omit, startCase } from 'lodash';
import { FormGroup } from '@angular/forms';
import {
    addContainerClassesNoRules,
    createUiTypeSelectorString,
    multiClasses,
} from '@dat/iq-style-processor';
import { HttpClient } from '@angular/common/http';
import { DatiqAppConfig } from '@dat/datiq-core/models';

const API_KEY = '2:5PnVejrP9cczduqWB7xv11lMx1Q7oqjiJNCAYCMOlzXFgzBRxsK4';

@Component({
    selector: 'iq-trendlines-content-view-trends',
    standalone: true,
    imports: [CommonModule, IqControlsDashboardComponent, CdkDrag],
    templateUrl: './iq-trendlines-content-view-trends.component.html',
    styleUrl: './iq-trendlines-content-view-trends.component.scss',
})
export class IqTrendlinesContentViewTrendsComponent {
    fg = new FormGroup({});
    private _http: HttpClient = inject(HttpClient);
    private _config: DatiqAppConfig = inject(DatiqAppConfig);
    controls: Signal<Control[]> = computed(() => {
        const featureFlagVariables = this._featureFlag.getEnabledVariables([
            'iq_com_trendline_weekly_brief',
        ])();
        const variables = featureFlagVariables['iq_com_trendline_weekly_brief'];

        const editableControls: Control[] = flatten(
            Object.entries(variables ?? {}).map(([flagName, value]): Control[] => {
                return [
                    {
                        id: `${flagName}-label`,
                        type: 'Label',
                        label: startCase(flagName),
                        name: `${flagName}-label`,
                        classes: {
                            ...multiClasses(
                                [
                                    'font-body-1-strong',
                                    'my-2',
                                    'text-dat-grey-800',
                                ],
                                [createUiTypeSelectorString('control-label')],
                            ),
                        },
                    },
                    {
                        id: flagName,
                        type: 'Text',
                        name: flagName,
                        value: value as string,
                        required: true,
                        classes: {
                            ...multiClasses(
                                ['rounded-sm', 'px-2', 'py-1', 'mb-2'],
                                ['[data-uitype="textBox"]'],
                            ),
                        },
                    },
                ];
            }),
        );
        const submit: Control = {
            id: 'view-trends-edit-submit',
            type: 'Submit',
            label: 'Update Content',
            classes: {
                ...addContainerClassesNoRules(['block', 'mt-4']),
                ...multiClasses(
                    [
                        'font-button-uppercase',
                        'rounded-full',
                        'px-4',
                        'py-2',
                        'bg-dat-blue-500',
                        'hover:bg-dat-blue-600',
                        'text-white',
                        'cursor-pointer',
                    ],
                    [createUiTypeSelectorString('button')],
                ),
            },
        };

        return [...editableControls, submit];
    });

    private _featureFlag: DatiqFeatureFlagStateService = inject(
        DatiqFeatureFlagStateService,
    );

    constructor() {
    }

    collapsed: WritableSignal<boolean> = signal(false);

    async onSubmit(values) {
        if (!this._config.optimizelyConfig.apiKey) return;

        const formData = omit(values.form, ['valid', 'errors']);
        const apiKey: string = this._config.optimizelyConfig.apiKey;
        const project_id = `20981000622`;
        const flag_key = `iq_com_trendline_weekly_brief`;
        const bodyData = flatten(
            Object.entries(formData).map(([key, value]) => [
                { op: 'remove', path: `/on/variables/${key}/revision` },
                {
                    op: 'replace',
                    path: `/on/variables/${key}/value`,
                    value,
                },
            ]),
        );
        const options = {
            method: 'PATCH',
            headers: {
                'content-type': 'application/json-patch+json',
                Authorization: `Bearer ${apiKey}`,
            },
            body: JSON.stringify(bodyData, null, 4),
        };
        const uri = `https://api.optimizely.com/flags/projects/${project_id}/flags/${flag_key}/variations`;
        const res = await fetch(uri, {
            ...options,
            headers: {
                ...options.headers,
            },
        });
        const body = await res.json();
    }
}
